import React, { useState } from 'react';
import { clsx } from 'clsx';
import {
  Play, Calendar, Globe, Webhook, Zap,
  Users, Server, HardDrive, Shield, Bell, Ticket,
  GitBranch, RefreshCw, Layers, Clock, CheckSquare,
  MessageSquare, Mail, Bot, Search, ChevronDown, ChevronRight,
} from 'lucide-react';

interface PaletteNode {
  type: string;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  bg: string;
}

interface PaletteGroup {
  label: string;
  color: string;
  nodes: PaletteNode[];
}

const PALETTE_GROUPS: PaletteGroup[] = [
  {
    label: 'Triggers',
    color: 'text-emerald-400',
    nodes: [
      { type: 'trigger_manual', label: 'Manual', description: 'Start manually', icon: Play, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
      { type: 'trigger_schedule', label: 'Schedule', description: 'CRON / interval', icon: Calendar, color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
      { type: 'trigger_event', label: 'Event', description: 'System event', icon: Zap, color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
      { type: 'trigger_api', label: 'API Call', description: 'REST trigger', icon: Globe, color: 'text-blue-400', bg: 'bg-blue-500/10' },
      { type: 'trigger_webhook', label: 'Webhook', description: 'Inbound webhook', icon: Webhook, color: 'text-purple-400', bg: 'bg-purple-500/10' },
    ],
  },
  {
    label: 'Active Directory',
    color: 'text-blue-400',
    nodes: [
      { type: 'action_ad', label: 'AD Action', description: 'User/Group/DNS', icon: Users, color: 'text-blue-400', bg: 'bg-blue-500/10' },
    ],
  },
  {
    label: 'Virtualization',
    color: 'text-purple-400',
    nodes: [
      { type: 'action_vm', label: 'VM Action', description: 'vCenter/vSphere', icon: Server, color: 'text-purple-400', bg: 'bg-purple-500/10' },
    ],
  },
  {
    label: 'Storage',
    color: 'text-orange-400',
    nodes: [
      { type: 'action_storage', label: 'Storage Action', description: 'NetApp/Pure/Veeam', icon: HardDrive, color: 'text-orange-400', bg: 'bg-orange-500/10' },
    ],
  },
  {
    label: 'Security',
    color: 'text-red-400',
    nodes: [
      { type: 'action_security', label: 'Security Action', description: 'Firewall/EDR/SIEM', icon: Shield, color: 'text-red-400', bg: 'bg-red-500/10' },
    ],
  },
  {
    label: 'Control Flow',
    color: 'text-amber-400',
    nodes: [
      { type: 'control_condition', label: 'Condition (If/Else)', description: 'Branch based on condition', icon: GitBranch, color: 'text-amber-400', bg: 'bg-amber-500/10' },
      { type: 'control_loop', label: 'Loop (For Each)', description: 'Iterate over collection', icon: RefreshCw, color: 'text-amber-400', bg: 'bg-amber-500/10' },
      { type: 'control_parallel', label: 'Parallel', description: 'Execute in parallel', icon: Layers, color: 'text-amber-400', bg: 'bg-amber-500/10' },
      { type: 'control_wait', label: 'Wait / Delay', description: 'Pause execution', icon: Clock, color: 'text-amber-400', bg: 'bg-amber-500/10' },
      { type: 'control_approval', label: 'Approval Gate', description: 'Human approval required', icon: CheckSquare, color: 'text-indigo-400', bg: 'bg-indigo-500/10' },
    ],
  },
  {
    label: 'Integrations',
    color: 'text-teal-400',
    nodes: [
      { type: 'integration_servicenow', label: 'ServiceNow', description: 'ITSM / CMDB', icon: Ticket, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
      { type: 'integration_slack', label: 'Slack', description: 'Messaging', icon: MessageSquare, color: 'text-pink-400', bg: 'bg-pink-500/10' },
      { type: 'integration_teams', label: 'Teams', description: 'Microsoft Teams', icon: MessageSquare, color: 'text-blue-400', bg: 'bg-blue-500/10' },
      { type: 'integration_email', label: 'Email', description: 'SMTP notifications', icon: Mail, color: 'text-gray-400', bg: 'bg-gray-500/10' },
      { type: 'action_notification', label: 'Notification', description: 'Multi-channel alert', icon: Bell, color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
    ],
  },
  {
    label: 'AI / Intelligence',
    color: 'text-violet-400',
    nodes: [
      { type: 'ai_analyze', label: 'AI Analyze', description: 'Incident analysis & triage', icon: Bot, color: 'text-violet-400', bg: 'bg-violet-500/10' },
      { type: 'ai_decide', label: 'AI Decide', description: 'Intelligent routing', icon: Bot, color: 'text-violet-400', bg: 'bg-violet-500/10' },
      { type: 'ai_generate', label: 'AI Generate', description: 'Content/command generation', icon: Bot, color: 'text-violet-400', bg: 'bg-violet-500/10' },
    ],
  },
];

interface NodePaletteProps {
  onDragStart: (event: React.DragEvent, nodeType: string, label: string) => void;
}

export function NodePalette({ onDragStart }: NodePaletteProps) {
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const filteredGroups = PALETTE_GROUPS.map((group) => ({
    ...group,
    nodes: group.nodes.filter(
      (n) =>
        n.label.toLowerCase().includes(search.toLowerCase()) ||
        n.description.toLowerCase().includes(search.toLowerCase()),
    ),
  })).filter((g) => g.nodes.length > 0);

  return (
    <div className="w-56 bg-white border-r border-gray-200 flex flex-col overflow-hidden">
      <div className="p-3 border-b border-gray-200">
        <div className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2">Node Palette</div>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input
            type="text"
            placeholder="Search nodes..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-gray-50 border border-gray-200 rounded-lg pl-8 pr-3 py-1.5 text-xs text-white placeholder-white/30 focus:outline-none focus:border-orbayx-500 transition-all"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto py-2">
        {filteredGroups.map((group) => (
          <div key={group.label} className="mb-1">
            <button
              onClick={() => setCollapsed((c) => ({ ...c, [group.label]: !c[group.label] }))}
              className={clsx('flex items-center justify-between w-full px-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest hover:bg-gray-50 transition-colors', group.color)}
            >
              <span>{group.label}</span>
              {collapsed[group.label] ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
            {!collapsed[group.label] && (
              <div className="px-2 pb-1 space-y-1">
                {group.nodes.map((node) => {
                  const Icon = node.icon;
                  return (
                    <div
                      key={node.type}
                      draggable
                      onDragStart={(e) => onDragStart(e, node.type, node.label)}
                      className={clsx(
                        'flex items-center gap-2 p-2 rounded-lg cursor-grab active:cursor-grabbing border border-transparent hover:border-gray-200 hover:bg-gray-50 transition-all select-none',
                      )}
                    >
                      <div className={clsx('p-1.5 rounded flex-shrink-0', node.bg)}>
                        <Icon className={clsx('w-3 h-3', node.color)} />
                      </div>
                      <div className="min-w-0">
                        <div className="text-xs font-medium text-gray-700 truncate">{node.label}</div>
                        <div className="text-[9px] text-gray-400 truncate">{node.description}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
